// @ts-nocheck

import { typography, mediaQueries, colors } from '@everlywell/leaves';
import { graphql, PageProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect } from 'react';
import styled from 'styled-components';

import CompanyDescription from '../components/CompanyDescription';
import Container from '../components/Container';
import Hero from '../components/Hero';
import Layout from '../components/Layout';
import PageBody from '../components/Page/PageBody';
import SEO, { Head as SEOHead } from '../components/SEO';
import SubcategoryList from '../components/Subcategory/SubcategoryList';
import { track } from '../utils/analytics';
import { ANALYTICS } from '../utils/consts/analytics';
import config from '../utils/siteConfig';

const RowContainer = styled.div`
  ${mediaQueries.forTabletHorizontalUp} {
    margin-top: 80px;
  }
  ${mediaQueries.forDesktopUp} {
    margin-top: 104px;
  }
`;

const DescriptionWrapper = styled.div`
  width: 100%;
`;

const Title = styled.h1`
  font-family: ${typography.type.ppAgrandir};
  font-weight: ${typography.weight.regular};
  font-size: 28.8px;
  line-height: 1.48;
  letter-spacing: 0.5px;
  font-style: normal;
  font-stretch: normal;
  color: ${colors.teal4};
  ${mediaQueries.forTabletHorizontalUp} {
    font-size: 32.4px;
  }
`;

interface SEOMapping {
  keywords: string[];
  url: string;
}

/**
 * Replaces keywords in HTML content with anchor tags, but only the first occurrence
 * of each keyword across all paragraphs. The keyword's original casing is preserved.
 * 
 * @param {string} content - The HTML content containing paragraphs where replacements will occur.
 * @param {Array<Object>} mappings - An array of mapping objects where each object contains:
 *   @param {Array<string>} mappings[].keywords - An array of keywords to be replaced.
 *   @param {string} mappings[].url - The URL that will be used in the anchor tag for the keyword.
 *
 * @returns {string} - The updated HTML content with the first occurrence of each keyword
 *                     replaced by an anchor tag with the keyword's original casing preserved.
 *
 * @example
 * const content = `
 *   <p>This is a sample paragraph with the keyword JavaScript.</p>
 *   <p>Another paragraph mentioning javascript and TypeScript.</p>
 * `;
 * const mappings = [
 *   { keywords: ['JavaScript'], url: 'https://www.javascript.com' },
 *   { keywords: ['TypeScript'], url: 'https://www.typescriptlang.org' }
 * ];
 * const result = replaceKeywordsWithAnchors(content, mappings);
 * console.log(result);
 * // Output:
 * // <p>This is a sample paragraph with the keyword <a href="https://www.javascript.com">JavaScript</a>.</p>
 * // <p>Another paragraph mentioning javascript and <a href="https://www.typescriptlang.org">TypeScript</a>.</p>
 *

 */
function replaceKeywordsWithAnchors(
  content: string,
  mappings: SEOMapping[],
): string {
  let updatedContent = content;
  const replacedKeywords = new Set<string>(); // Track replaced keywords globally across all paragraphs

  mappings?.forEach((mapping) => {
    mapping?.keywords?.forEach((keyword) => {
      const escapedKeyword = keyword?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(
        `\\b(?!<a[^>]*?>)(${escapedKeyword})(?![^<]*?</a>)\\b`,
        'gi',
      );

      updatedContent = updatedContent?.replace(
        // Match each paragraph and ignore any other HTML tags like h2
        /<p>(.*?)<\/p>/gi,
        (paragraphMatch) => {
          // Skip this keyword if it's already replaced globally
          if (replacedKeywords.has(keyword)) {
            return paragraphMatch;
          }

          // Replace only the first occurrence within this paragraph
          let keywordReplacedInParagraph = false;

          const isEverlywellLink = mapping?.url?.includes('everlywell.com');

          const newParagraph = paragraphMatch?.replace(regex, (match) => {
            if (!keywordReplacedInParagraph) {
              keywordReplacedInParagraph = true; // Mark locally replaced
              replacedKeywords.add(keyword); // Mark globally replaced
              // Use the original match (preserving the original casing)
              return `<a href="${mapping.url}" ${
                // Open the link in a new tab if it's not an Everlywell link
                isEverlywellLink
                  ? ''
                  : 'target="_blank" rel="noopener noreferrer"'
              }>${match}</a>`;
            }
            return match; // Return original match for subsequent occurrences
          });

          return newParagraph;
        },
      );
    });
  });

  return updatedContent;
}

interface PostData {
  contentfulPost: {
    title: string;
    metaTitle?: string;
    slug: string;
    canonicalUrl?: string;
    faqSchema?: Array<{
      question: string;
      answer: string;
    }>;
    description: {
      internal: {
        content: string;
      };
    };
    publishDate: string;
    publishDateISO: string;
    subcategories: Array<{
      title: string;
      id: string;
      slug: string;
    }>;
    heroImage: {
      title: string;
      gatsbyImageData: IGatsbyImageData;
      description: string;
    };
    body: {
      childMarkdownRemark: {
        html: string;
        excerpt: string;
      };
    };
  };
}

interface PageContext {
  slug: string;
  seoMap: SEOMapping[];
}

interface HeadProps {
  data: PostData;
}

export const Head: React.FC<HeadProps> = ({ data }) => {
  const postNode = data.contentfulPost;
  const { title, metaTitle, slug } = postNode;

  return (
    <>
      <title>{metaTitle || `${title} - ${config.siteTitle}`}</title>
      <SEOHead postNode={postNode} pagePath={slug} postSEO={true} />
    </>
  );
};

const PostTemplate: React.FC<PageProps<PostData, PageContext>> = ({
  data,
  pageContext,
}) => {
  const {
    title,
    slug,
    canonicalUrl,
    faqSchema,
    heroImage,
    body,
    subcategories,
  } = data.contentfulPost;
  const postNode = data.contentfulPost;

  const result = replaceKeywordsWithAnchors(
    body.childMarkdownRemark.html,
    pageContext.seoMap,
  );

  const updatedBody = {
    ...body,
    childMarkdownRemark: {
      html: result,
    },
  };

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_BLOG_PAGE,
      data: {
        label: 'Blog Post',
        blog: title,
      },
    });
  }, [title]);

  return (
    <Layout>
      <SEO
        pagePath={slug}
        postNode={postNode}
        postSEO
        canonicalUrl={canonicalUrl}
        faqSchema={faqSchema}
      />
      <Container>
        <RowContainer className="row">
          <div className="col-xs-12 col-sm-12 col-md-8">
            <Hero image={heroImage} />
            {subcategories && <SubcategoryList subcategories={subcategories} />}
            <Title>{title}</Title>
            <PageBody body={updatedBody} />
          </div>
          <div className="col-xs-12 col-sm-12 col-md-4 first-md">
            <DescriptionWrapper>
              <CompanyDescription />
            </DescriptionWrapper>
          </div>
        </RowContainer>
      </Container>
    </Layout>
  );
};

export const query = graphql`
  query ($slug: String!) {
    contentfulPost(slug: { eq: $slug }) {
      title
      metaTitle
      slug
      canonicalUrl
      faqSchema {
        question
        answer
      }
      description {
        internal {
          content
        }
      }
      publishDate(formatString: "MMMM DD, YYYY")
      publishDateISO: publishDate(formatString: "YYYY-MM-DD:HH:MM:SSZ")
      subcategories {
        title
        id
        slug
      }
      heroImage {
        title
        gatsbyImageData(placeholder: BLURRED)
        description
      }
      body {
        childMarkdownRemark {
          html
          excerpt(pruneLength: 320)
        }
      }
    }
  }
`;

export default PostTemplate;
